import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { InstructorAutocomplete } from '@/components/ui/InstructorAutocomplete';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, X, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useI18n } from '@/contexts/I18nContext';
import { useTechniques, getTechniqueName, getCategoryName, sortTechniqueCategories } from '@/shared/hooks/useTechniques';

import { TRAINING_CATEGORIES as trainingCategories, getTrainingCategoryTranslationKey } from '@/constants/trainingCategories';

// Modalities
const modalities = ['Gi', 'No-Gi'];


export interface TrainingFilters {
  types: string[];
  modalities: string[];
  startDate: Date | string | null;
  endDate: Date | string | null;
  instructor: any | null;
  statuses: string[];
  techniques: string[];
  categories: string[];
}

interface TrainingFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: TrainingFilters) => void;
  onClearFilters: () => void;
  currentFilters: TrainingFilters;
  hasActiveFilters: boolean;
}

export const TrainingFilterModal: React.FC<TrainingFilterModalProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  onClearFilters,
  currentFilters,
  hasActiveFilters
}) => {
  const [filters, setFilters] = useState<TrainingFilters>(currentFilters);
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const [showTechniques, setShowTechniques] = useState(false);
  const { t, currentLanguage } = useI18n();
  const { data: techniqueCategories, isLoading: techniquesLoading } = useTechniques();

  // Training types based on the actual database values (translated)
  const trainingTypes = [
    t('calendar.group_class'),
    t('calendar.private_class')
  ];

  useEffect(() => {
    if (isOpen) {
      setFilters(currentFilters);
    }
  }, [isOpen]);

  const handleFilterChange = (key: keyof TrainingFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleTypeToggle = (type: string) => {
    setFilters(prev => ({
      ...prev,
      types: prev.types.includes(type)
        ? prev.types.filter(t => t !== type)
        : [...prev.types, type]
    }));
  };

  const handleModalityToggle = (modality: string) => {
    setFilters(prev => ({
      ...prev,
      modalities: prev.modalities.includes(modality)
        ? prev.modalities.filter(m => m !== modality)
        : [...prev.modalities, modality]
    }));
  };

  const handleStatusToggle = (status: string) => {
    setFilters(prev => ({
      ...prev,
      statuses: prev.statuses.includes(status)
        ? prev.statuses.filter(s => s !== status)
        : [...prev.statuses, status]
    }));
  };

  const handleTechniqueToggle = (technique: string) => {
    setFilters(prev => ({
      ...prev,
      techniques: prev.techniques.includes(technique)
        ? prev.techniques.filter(t => t !== technique)
        : [...prev.techniques, technique]
    }));
  };

  const handleCategoryToggle = (category: string) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const toggleCategory = (categoryName: string) => {
    setOpenCategories(prev => {
      if (prev.includes(categoryName)) {
        return prev.filter(cat => cat !== categoryName);
      } else {
        return [...prev, categoryName];
      }
    });
  };

  const isCategoryOpen = (categoryName: string) => {
    return openCategories.includes(categoryName);
  };

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleClear = () => {
    const emptyFilters: TrainingFilters = {
      types: [],
      modalities: [],
      startDate: null,
      endDate: null,
      instructor: null,
      statuses: [],
      techniques: [],
      categories: [] // Clear categories as well
    };
    setFilters(emptyFilters);
    onClearFilters();
    onClose();
  };

  const handleClose = () => {
    setFilters(currentFilters); // Reset to current filters
    onClose();
  };

  const statusOptions = [t('calendar.completed'), t('calendar.missed'), t('calendar.scheduled')];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="w-[90vw] max-w-2xl max-h-[85vh] bg-white rounded-2xl p-6 overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-black">
            {t('calendar.filters')}
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            {t('calendar.customize_training_view')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 flex-1 overflow-y-auto pr-2">
          {/* Training Type */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              {t('calendar.training_type')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {trainingTypes.map((type) => (
                <div key={type} className="flex items-center space-x-2">
                  <Checkbox
                    id={`type-${type}`}
                    checked={filters.types.includes(type)}
                    onCheckedChange={() => handleTypeToggle(type)}
                  />
                  <label htmlFor={`type-${type}`} className="text-xs text-black">
                    {type}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Modality */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              {t('calendar.modality')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {modalities.map((modality) => (
                <div key={modality} className="flex items-center space-x-2">
                  <Checkbox
                    id={`modality-${modality}`}
                    checked={filters.modalities.includes(modality)}
                    onCheckedChange={() => handleModalityToggle(modality)}
                  />
                  <label htmlFor={`modality-${modality}`} className="text-xs text-black">
                    {modality}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Period */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              {t('calendar.period')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">{t('calendar.start_date')}</label>
                <Input
                  type="date"
                  value={filters.startDate ? (typeof filters.startDate === 'string' ? filters.startDate : format(filters.startDate, "yyyy-MM-dd")) : ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      // Use the date string directly without parsing to avoid issues
                      handleFilterChange('startDate', value);
                    } else {
                      handleFilterChange('startDate', null);
                    }
                  }}
                  className="text-black border-black bg-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">{t('calendar.end_date')}</label>
                <Input
                  type="date"
                  value={filters.endDate ? (typeof filters.endDate === 'string' ? filters.endDate : format(filters.endDate, "yyyy-MM-dd")) : ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value) {
                      // Use the date string directly without parsing to avoid issues
                      handleFilterChange('endDate', value);
                    } else {
                      handleFilterChange('endDate', null);
                    }
                  }}
                  className="text-black border-black bg-white text-sm"
                />
              </div>
            </div>
          </div>

          {/* Training Category */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              {t('calendar.training_category')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {trainingCategories.map((category) => {
                const categoryKey = getTrainingCategoryTranslationKey(category);
                return (
                  <div key={category} className="flex items-center space-x-2">
                    <Checkbox
                      id={`category-${category}`}
                      checked={filters.categories.includes(category)}
                      onCheckedChange={() => handleCategoryToggle(category)}
                    />
                    <label htmlFor={`category-${category}`} className="text-xs text-black">
                      {t(`calendar.training_categories.${categoryKey}`) || category}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Instructor */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('profile.instructor')}</h3>
            <InstructorAutocomplete
              value={filters.instructor}
              onChange={(instructor) => handleFilterChange('instructor', instructor)}
              placeholder={t('calendar.search_instructor_placeholder')}
              className="text-black border-black"
              containerClassName="bg-white"
              noPortal
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium mb-3 text-black">
              {t('common.status')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {statusOptions.map((status) => (
                <div key={status} className="flex items-center space-x-2">
                  <Checkbox
                    id={`status-${status}`}
                    checked={filters.statuses.includes(status)}
                    onCheckedChange={() => handleStatusToggle(status)}
                  />
                  <label htmlFor={`status-${status}`} className="text-xs text-black">
                    {status}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Techniques */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-black">
                {t('calendar.techniques')}
              </label>
              <button
                type="button"
                onClick={() => setShowTechniques(!showTechniques)}
                className="text-xs text-sossiety-red hover:text-sossiety-red-hover font-medium underline"
              >
                {showTechniques ? t('calendar.hide') : t('calendar.show')} {t('calendar.techniques')}
              </button>
            </div>
            
            {!showTechniques && filters.techniques.length === 0 && (
              <div className="text-center mt-3 mb-3">
                <p className="text-xs font-medium text-gray-600">
                  {t('calendar.techniques_filter_description')}
                </p>
              </div>
            )}

            {filters.techniques.length > 0 && (
              <div className="mb-3">
                <div className="text-xs text-gray-600 mb-2">
                  {t('calendar.selected')}: {filters.techniques.length} {t('calendar.technique')}{filters.techniques.length !== 1 ? 's' : ''}
                </div>
                <div className="flex flex-wrap gap-1">
                  {filters.techniques.map((tech) => (
                    <span
                      key={tech}
                      className="px-2 py-1 bg-sossiety-red text-white rounded-full text-xs font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {showTechniques && (
              <div className="space-y-3 mt-4">
                {techniquesLoading ? (
                  <div className="text-center py-4 text-gray-500">{t('calendar.loading_techniques')}</div>
                ) : (
                  sortTechniqueCategories(techniqueCategories as any[])?.map((category) => (
                  <Collapsible 
                    key={category.name} 
                    open={isCategoryOpen(category.name)} 
                    onOpenChange={() => toggleCategory(category.name)}
                  >
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-left text-black bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                      <span>{currentLanguage === 'pt' ? category.name_br : category.name}</span>
                      {isCategoryOpen(category.name) ? (
                        <ChevronUp className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pt-2">
                      <div className="flex flex-wrap gap-1">
                        {category.techniques.map((technique) => {
                          const isSelected = filters.techniques.includes(technique.name);
                          return (
                            <button
                              key={technique.id}
                              type="button"
                              onClick={() => handleTechniqueToggle(technique.name)}
                              className={`px-2 py-1 text-xs font-medium rounded border transition-colors duration-200 ${
                                isSelected
                                  ? 'bg-sossiety-red text-white border-sossiety-red'
                                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                              }`}
                            >
                              {getTechniqueName(technique, currentLanguage)}
                            </button>
                          );
                        })}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200 flex gap-3">
          <Button
            variant="outline-light"
            size="lg"
            onClick={handleClear}
            className="flex-1"
          >
            {t('common.clear')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleApply}
            className="flex-1"
          >
            {t('common.apply')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 